/**
 * @file AddRelatedRecordModel.jsx
 * @description AddRelatedRecordModel component for displaying and managing add related record model.
 * @component AddRelatedRecordModel
 * @exports AddRelatedRecordModel - Main component for displaying and managing add related record model.
 */

import {
    Modal,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Alert,
 
  } from "@mui/material";
  import React, { useState } from "react";
  import {
    VARIANTS,
    BUTTON_COLORS,
    SIZES,
    STATUS,
    TYPOGRAPHY_VARIANT,
  } from "../../../common/constants/commonConstants";
  import {
    INCOMING_CALLS_STRINGS,
    INCOMING_CALLS_ERRORS,
    VALIDATION_MESSAGES,
    VALIDATION_PATTERNS,
    SEARCH_FILTER_OPTIONS,
    SEARCH_FILTER_LABELS,
    UI_CONFIG,
  } from "../../../common/constants/agentModule/incomingCallsConstants";
import { AddCustomerRelatedRecord } from "../../../services/customerinfoModule/profileServices";
import ProgressOverlay from "../../../common/components/ProgressOverlay";
 
  
  const AddRelatedRecordModel = ({ open, onClose, customerguid }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [searchText, setSearchText] = useState("");
    const [error, setError] = useState("");
  
    const validateSearchText = () => {
      if (!searchText.trim()) {
        setError(VALIDATION_MESSAGES.SEARCH_TEXT_REQUIRED);
        return false;
      }
  
      if (!filter) {
        setError(VALIDATION_MESSAGES.SELECT_FILTER);
        return false;
      }
  
      switch (filter) {
        case SEARCH_FILTER_OPTIONS.LAST_NAME: {
          const nameRegex = VALIDATION_PATTERNS.LAST_NAME;
          if (!nameRegex.test(searchText.trim())) {
            setError(VALIDATION_MESSAGES.INVALID_LAST_NAME);
            return false;
          }
          break;
        }
        case SEARCH_FILTER_OPTIONS.PHONE_NUMBER: {
          const phoneRegex = VALIDATION_PATTERNS.PHONE_NUMBER;
          const cleanPhone = searchText.replace(/[\s\-\(\)]/g, "");
          if (!phoneRegex.test(cleanPhone)) {
            setError(VALIDATION_MESSAGES.INVALID_PHONE_NUMBER);
            return false;
          }
          break;
        }
        case SEARCH_FILTER_OPTIONS.EMAIL: {
          const emailRegex = VALIDATION_PATTERNS.EMAIL;
          if (!emailRegex.test(searchText.trim())) {
            setError(VALIDATION_MESSAGES.INVALID_EMAIL);
            return false;
          }
          break;
        }
        default:
          setError(VALIDATION_MESSAGES.INVALID_FILTER);
          return false;
      }
  
      setError("");
      return true;
    };
  
    const handleAddRelatedRecord = async () => {
      if (!validateSearchText()) {
        return;
      }
  
      setIsLoading(true);
  
      try {
         await AddCustomerRelatedRecord({
            customerguid,
            type: searchText,
            typeValue: filter,
          });
          onClose();
      } catch (error) {
        console.error("Search error:", error);
        setError(INCOMING_CALLS_ERRORS.SEARCH_FAILED);
      } finally {
        setIsLoading(false);
      }
    };
  
  
    const handleClose = () => {
      setError("");
      onClose();
    };
  
    const getPlaceholder = () => {
      switch (filter) {
        case SEARCH_FILTER_OPTIONS.LAST_NAME:
          return INCOMING_CALLS_STRINGS.ENTER_LAST_NAME;
        case SEARCH_FILTER_OPTIONS.PHONE_NUMBER:
          return INCOMING_CALLS_STRINGS.ENTER_PHONE_NUMBER;
        case SEARCH_FILTER_OPTIONS.EMAIL:
          return INCOMING_CALLS_STRINGS.ENTER_EMAIL_ADDRESS;
        default:
          return INCOMING_CALLS_STRINGS.ENTER_SEARCH_TEXT;
      }
    };
  
    return (
      <div>
        {isLoading && (<ProgressOverlay />)}
        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              width: UI_CONFIG.MODAL_WIDTH,
              maxHeight: "80vh",
              margin: "auto",
              padding: UI_CONFIG.MODAL_PADDING,
              marginTop: UI_CONFIG.MODAL_MARGIN_TOP,
              backgroundColor: "white",
              borderRadius: UI_CONFIG.MODAL_BORDER_RADIUS,
              overflow: "auto",
            }}
          >
            <Typography variant={TYPOGRAPHY_VARIANT.H6}>
              {INCOMING_CALLS_STRINGS.ADD_RELATED_RECORDS_TITLE}
            </Typography>
  
            {error && (
              <Alert
                severity={STATUS.ERROR}
                sx={{ mt: UI_CONFIG.MARGIN_TOP, mb: 1 }}
              >
                {error}
              </Alert>
            )}
  
            <FormControl
              variant={VARIANTS.OUTLINED}
              size={SIZES.SMALL}
              sx={{ width: "100%", mt: UI_CONFIG.MARGIN_TOP }}
            >
              <InputLabel>Add as</InputLabel>
              <Select
                size={SIZES.SMALL}
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setError(""); // Clear error when filter changes
                }}
                label="Add as"
              >
                <MenuItem value={SEARCH_FILTER_OPTIONS.LAST_NAME}>
                  {SEARCH_FILTER_LABELS[SEARCH_FILTER_OPTIONS.LAST_NAME]}
                </MenuItem>
                <MenuItem value={SEARCH_FILTER_OPTIONS.PHONE_NUMBER}>
                  {SEARCH_FILTER_LABELS[SEARCH_FILTER_OPTIONS.PHONE_NUMBER]}
                </MenuItem>
                <MenuItem value={SEARCH_FILTER_OPTIONS.EMAIL}>
                  {SEARCH_FILTER_LABELS[SEARCH_FILTER_OPTIONS.EMAIL]}
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              variant={VARIANTS.OUTLINED}
              size={SIZES.SMALL}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setError(""); // Clear error when text changes
              }}
              placeholder={getPlaceholder()}
              sx={{ my: 2 }}
            />
  
            <Button
              variant={VARIANTS.CONTAINED}
              color={BUTTON_COLORS.PRIMARY}
              onClick={handleAddRelatedRecord}
              disabled={isLoading}
              sx={{ mr: 1 }}
            >
              {isLoading
                ? INCOMING_CALLS_STRINGS.ADDING
                : INCOMING_CALLS_STRINGS.ADD}
            </Button>
            <Button variant={VARIANTS.OUTLINED} onClick={handleClose}>
              {INCOMING_CALLS_STRINGS.CANCEL}
            </Button>
  
         
          </Box>
        </Modal>
      </div>
    );
  };
  
  export default AddRelatedRecordModel;
  